// Secure fetch helper
async function fetchContent(url) {
  try {
    const response = await fetch(url, {
      headers: { 'Accept': 'text/html' }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.text();

  } catch (error) {
    console.error('Error fetching URL:', url, error);
    return `<div class="error">Failed to load content from ${url}</div>`;
  }
}

// Exported function for handling reading pane link clicks (for React integration)
export const handleReadingPaneLinkClick = async (href, options = {}) => {
  const {
    modalBody = document.querySelector('#readingPaneModalBody'),
    modal = document.querySelector('#readingPaneModal'),
    documentListing = document.querySelectorAll('.document-list'),
    documentListingSubtitle = document.querySelectorAll('.document-listing__subtitle'),
    onLoadingStart,
    onLoadingComplete,
    onError
  } = options;

  if (!modalBody || !modal) {
    const error = new Error('Modal or modal body element not found');
    console.error(error.message);
    if (onError) onError(error);
    return;
  }

  try {
    // Call loading callback if provided
    if (onLoadingStart) onLoadingStart();
    
    modalBody.textContent = 'Loading...';

    const url = `${window.location.origin}${href.startsWith('/') ? '' : '/'}${href}`;

    const html = await fetchContent(url);
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const body = doc.querySelector('body');

    modalBody.textContent = '';
    if (body) {
      Array.from(body.children).forEach(node => {
        modalBody.appendChild(node.cloneNode(true));
      });
    }

    modal.classList.add('show');
    if (documentListing) {
      [...documentListing].forEach(documentListingEl =>
        documentListingEl.style.display = 'none',
      );
    }

    if (documentListingSubtitle) {
      [...documentListingSubtitle].forEach(subtitleEl =>
        subtitleEl.style.display = 'none',
      );
    }

    // Call completion callback if provided
    if (onLoadingComplete) onLoadingComplete();

  } catch (error) {
    console.error('Error fetching content:', error);
    modalBody.textContent = 'Error loading content. Please try again.';
    if (onError) onError(error);
  }
};

// Exported function for handling print all functionality (for React integration)
export const handlePrintAllReadingPaneLinks = async (links, options = {}) => {
  const {
    modalBody = document.querySelector('#readingPaneModalBody'),
    modal = document.querySelector('#readingPaneModal'),
    documentListing = document.querySelectorAll('.document-list'),
    documentListingSubtitle = document.querySelectorAll('.document-listing__subtitle'),
    onLoadingStart,
    onLoadingComplete,
    onError,
    autoPrint = true
  } = options;

  if (!modalBody || !modal) {
    const error = new Error('Modal or modal body element not found');
    console.error(error.message);
    if (onError) onError(error);
    return;
  }

  try {
    // Call loading callback if provided
    if (onLoadingStart) onLoadingStart();
    
    modalBody.textContent = 'Preparing content for print...';

    // If links is not provided, get all reading pane links from DOM
    const linksToProcess = links || Array.from(document.querySelectorAll('.js-reading-pane-link'));

    const fetchPromises = linksToProcess.map(link => {
      const href = typeof link === 'string' ? link : link.getAttribute('href');
      const url = `${window.location.origin}${href.startsWith('/') ? '' : '/'}${href}`;
      return fetchContent(url);
    });

    const htmlChunks = await Promise.all(fetchPromises);

    modalBody.textContent = '';
    htmlChunks.forEach(html => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const body = doc.querySelector('body');

      if (body) {
        Array.from(body.children).forEach(node => {
          modalBody.appendChild(node.cloneNode(true));
        });
      }
    });

    modal.classList.add('show');
    if (documentListing) {
      [...documentListing].forEach(documentListingEl =>
        documentListingEl.style.display = 'none',
      );
    }
    if (documentListingSubtitle) {
      [...documentListingSubtitle].forEach(subtitleEl =>
        subtitleEl.style.display = 'none',
      );
    }

    if (autoPrint) {
      window.print();
    }

    // Call completion callback if provided
    if (onLoadingComplete) onLoadingComplete();

  } catch (error) {
    console.error('Error preparing print content:', error);
    modalBody.textContent = 'Failed to prepare content for print.';
    if (onError) onError(error);
  }
};

export default () => {
  const readingPane = document.querySelector('.js-reading-pane');
  const readingPaneList = document.querySelector('.reading-pane__list');
  const documentListing = document.querySelectorAll('.document-list');
  const documentListingSubtitle = document.querySelectorAll('.document-listing__subtitle');
  const modalBody = document.querySelector('#readingPaneModalBody');
  const modal = document.querySelector('#readingPaneModal');
  const closeButton = document.querySelector('.js-reading-pane-close');
  const printAllButton = document.querySelector('.js-print-all');

  if (!readingPane || !readingPaneList) return;

  // Build the reading pane links from document-list__item-title <a> elements
  const docLinks = document.querySelectorAll('.document-list__item-title');

  docLinks.forEach(linkEl => {
    const href = linkEl.getAttribute('href');
    const text = linkEl.textContent.trim();

    if (href && text) {
      const li = document.createElement('li');
      li.classList.add('reading-pane__list-item');

      const a = document.createElement('a');
      a.classList.add('js-reading-pane-link');
      a.setAttribute('href', href);
      a.textContent = text;

      li.appendChild(a);
      readingPaneList.appendChild(li);
    }
  });

  const readingPaneLinks = readingPaneList.querySelectorAll('.js-reading-pane-link');

  // Handle single item click
  readingPaneLinks.forEach(link => {
    link.addEventListener('click', async (event) => {
      event.preventDefault();
      const href = link.getAttribute('href');
      const separator = href.includes('?') ? '&' : '?';
      const hrefWithParam = `${href}${separator}bodyOnly=1`;

      await handleReadingPaneLinkClick(hrefWithParam);
    });
  });

  // Close modal
  if (closeButton && modal) {
    closeButton.addEventListener('click', () => {
      modal.classList.remove('show');
      if (documentListing) {
        [...documentListing].forEach(documentListingEl =>
          documentListingEl.style.display = '',
        );
      }
      if (documentListingSubtitle) {
        [...documentListingSubtitle].forEach(subtitleEl =>
          subtitleEl.style.display = '',
        );
      }
    });
  }

  // Print all items
  if (printAllButton && modalBody && modal) {
    printAllButton.addEventListener('click', async (event) => {
      event.preventDefault();
      await handlePrintAllReadingPaneLinks();
    });
  }
};
